import { stubTrue } from 'lodash';
import {
  ApiFilterOperation,
  HookedSource,
  Source,
  abortableFetch,
  listItemsFactory,
  useScrollableSource,
} from '@eas/common-web';
import {
  ACCESSORY_PROPOSER_SOURCE,
  CHALLENGED_ACT_SOURCE,
  COVER_LETTER_SOURCE,
  DOCUMENT_SOURCE,
  LEGAL_ADVISER_SOURCE,
  PROPOSER_SOURCE,
  REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE,
  REGULATION_EUROPEAN_UNION_LAW_SOURCE,
  REGULATION_OTHER_SOURCE,
  REGULATION_SOURCE,
} from '@modules/es-file/es-file-hooked-sources';
import { useHistorySource } from '@composite/history/history-api';
import { Decision, DocumentAttachment } from '@models';
import {
  EvidenceApiUrl,
  HistoryEntityType,
  ParticipantType,
  RegulationType,
} from '@enums';

export const NOTE_SOURCE = 'NOTE_SOURCE';

type CoverLetterResponse =
  | DocumentAttachment[]
  | Pick<
      Source<DocumentAttachment>,
      'aggregations' | 'highlights' | 'searchAfter' | 'items' | 'count'
    >
  | undefined;

function toCoverLetterSource(
  data: CoverLetterResponse
): Source<DocumentAttachment> {
  const result = Array.isArray(data)
    ? { items: data, count: data.length }
    : data ?? { items: [], count: 0 };

  return {
    ...result,
    loading: false,
    setLoading: () => undefined,
    reset: () => undefined,
  };
}

function useNoteSource(): HookedSource<Decision> {
  const source = useScrollableSource();

  return {
    [NOTE_SOURCE]: {
      source,
      shouldLoad: () => true,
      modifyBeforeLoad: ({ id }) => {
        source.setUrl(`${EvidenceApiUrl.DECISIONS}/${id}/note/list`);
      },
    },
  };
}

function useDocumentSource(): HookedSource<Decision> {
  const source = useScrollableSource({
    url: `${EvidenceApiUrl.DOCUMENTS}/list`,
  });

  return {
    [DOCUMENT_SOURCE]: {
      source,
      shouldLoad: ({ esFile }) => !!esFile?.id,
      modifyBeforeLoad: ({ esFile }) => {
        source.setParams({
          size: 50,
          filters: [
            {
              field: 'esFile.id',
              operation: ApiFilterOperation.EQ,
              value: esFile?.id,
            },
          ],
          sort: [
            {
              field: 'order',
              type: 'FIELD',
              order: 'DESC',
            },
          ],
        });
      },
    },
  };
}

function useCoverLetterSource(): HookedSource<Decision> {
  const source = useScrollableSource<DocumentAttachment>({
    listItems: listItemsFactory<DocumentAttachment>({
      listItemsMethod: (url) =>
        abortableFetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }),
      postProcess: async (data: CoverLetterResponse) =>
        toCoverLetterSource(data),
    }),
  });

  return {
    [COVER_LETTER_SOURCE]: {
      source,
      shouldLoad: ({ esFile }) => !!esFile?.id,
      modifyBeforeLoad: ({ esFile }) => {
        source.setUrl(`${EvidenceApiUrl.ES_FILES}/${esFile?.id}/cover-letter`);
      },
    },
  };
}

function useParticipantSource(
  sourceKey: string,
  participantType: ParticipantType,
  sortField: string
): HookedSource<Decision> {
  const source = useScrollableSource();

  return {
    [sourceKey]: {
      source,
      shouldLoad: ({ esFile }) => !!esFile?.id,
      modifyBeforeLoad: ({ esFile }) => {
        source.setUrl(
          `${EvidenceApiUrl.ES_FILES}/${esFile?.id}/participant/list`
        );
        source.setParams({
          size: 15,
          sort: [
            {
              field: sortField,
              order: 'ASC',
              type: 'FIELD',
            },
          ],
          filters: [
            {
              field: 'type.id',
              operation: ApiFilterOperation.EQ,
              value: participantType,
            },
          ],
        });
      },
    },
  };
}

function useChallengedActSource(): HookedSource<Decision> {
  const source = useScrollableSource();

  return {
    [CHALLENGED_ACT_SOURCE]: {
      source,
      shouldLoad: ({ esFile }) => !!esFile?.id,
      modifyBeforeLoad: ({ esFile }) => {
        source.setUrl(
          `${EvidenceApiUrl.ES_FILES}/${esFile?.id}/challenged-act/list`
        );
        source.setParams({
          sort: [
            {
              field: 'actType.name',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
        });
      },
    },
  };
}

function useRegulationSource(): HookedSource<Decision> {
  const source = useScrollableSource();

  return {
    [REGULATION_SOURCE]: {
      source,
      shouldLoad: ({ esFile }) => !!esFile?.id,
      modifyBeforeLoad: ({ esFile }) => {
        source.setUrl(
          `${EvidenceApiUrl.ES_FILES}/${esFile?.id}/regulation/list`
        );
        source.setParams({
          size: -1,
          sort: [
            {
              field: 'label',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
        });
      },
    },
  };
}

function useRegulationTypeSource(
  sourceKey: string,
  regulationType: RegulationType
): HookedSource<Decision> {
  const source = useScrollableSource();

  return {
    [sourceKey]: {
      source,
      shouldLoad: ({ esFile }) => !!esFile?.id,
      modifyBeforeLoad: ({ esFile }) => {
        source.setUrl(
          `${EvidenceApiUrl.ES_FILES}/${esFile?.id}/regulation/list`
        );
        source.setParams({
          size: -1,
          sort: [
            {
              field: 'valueSort',
              order: 'ASC',
              type: 'FIELD',
            },
          ],
          filters: [
            {
              field: 'regulationType.id',
              operation: ApiFilterOperation.EQ,
              value: regulationType,
            },
          ],
        });
      },
    },
  };
}

export function useDecisionsHookedSources(): HookedSource<Decision> {
  const notes = useNoteSource();
  const documents = useDocumentSource();
  const coverLetters = useCoverLetterSource();
  const proposers = useParticipantSource(
    PROPOSER_SOURCE,
    ParticipantType.PROPOSER,
    'created'
  );
  const accessoryProposers = useParticipantSource(
    ACCESSORY_PROPOSER_SOURCE,
    ParticipantType.ACCESSORY_PARTICIPANT,
    'contact.subject.name'
  );
  const legalAdvisers = useParticipantSource(
    LEGAL_ADVISER_SOURCE,
    ParticipantType.LEGAL_ADVISER,
    'contact.subject.name'
  );
  const challengedActs = useChallengedActSource();
  const regulations = useRegulationSource();
  const constitutionalRegulations = useRegulationTypeSource(
    REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE,
    RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES
  );
  const europeanUnionRegulations = useRegulationTypeSource(
    REGULATION_EUROPEAN_UNION_LAW_SOURCE,
    RegulationType.EUROPEAN_UNION_LAW
  );
  const otherRegulations = useRegulationTypeSource(
    REGULATION_OTHER_SOURCE,
    RegulationType.OTHER
  );
  const history = useHistorySource({
    shouldLoad: stubTrue,
    entityType: HistoryEntityType.DECISION,
  });

  return {
    ...notes,
    ...documents,
    ...coverLetters,
    ...proposers,
    ...accessoryProposers,
    ...legalAdvisers,
    ...challengedActs,
    ...regulations,
    ...constitutionalRegulations,
    ...europeanUnionRegulations,
    ...otherRegulations,
    ...history,
  };
}
